import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import DateTimeRangePicker, { DateTimeRangePickerProps } from '@wojtekmaj/react-datetimerange-picker'
import '@wojtekmaj/react-datetimerange-picker/dist/DateTimeRangePicker.css'
import 'react-calendar/dist/Calendar.css'
import 'react-clock/dist/Clock.css'
import dayjs from 'dayjs'
import cx from 'classnames'
import DatePickerUtils, {
  DATE_PICKER_CAL_CN,
  DATE_PICKER_CAL_FIXED_CN,
  DATE_PICKER_CN,
  DATE_PICKER_SIZE_MD_CN,
  DATE_PICKER_SIZE_LG_CN,
  DATE_PICKER_TILE_CN,
} from './DatePickerUtils'
import { compact, isEmpty } from 'lodash-es'
import { DatePickerCommonProps, DatePickerValue, VariableDatePickerValue } from './DatePickerInput'
import MToast from '@/utils/modal/MToast'
import './datePickerStyles.css'
import InputResetButton from './InputResetButton'

export type DateRangeValue = [DatePickerValue, DatePickerValue]
export interface DateRangePickerInputProps extends Omit<DateTimeRangePickerProps, 'onChange' | 'value' | 'size'>, DatePickerCommonProps {
  value?: [VariableDatePickerValue, VariableDatePickerValue]
  maxDateRange?: number
  height?: number
  onChange?: (v: DateRangeValue) => void
}

const uuid = () => '_' + Math.random().toString(36).substring(2, 11)

export function DateRangePickerInput(props: DateRangePickerInputProps) {
  const {
    showTimeSelect,
    showAmPm,
    fixedCalendar,
    size,
    placement = 'bottom',
    value,
    className,
    calendarClassName,
    maxDateRange,
    onChange,
    height = 28,
    ...rest
  } = props

  const id = useMemo(() => uuid(), [])
  const calCn = useMemo(() => `${id}__calendar`, [id])

  const [temp, setTemp] = useState<DateRangeValue>([null, null])
  // 마지막 상호작용 출처. 'calendar'(타일 클릭) 인 경우에만 onChange 를 즉시 폼에 반영하고,
  // 'input'(직접 타이핑) 인 경우엔 blur 시점까지 temp 에 버퍼링한다.
  // fixedCalendar(portal) 모드에선 캘린더가 #root 로 portal 되어 타일 클릭 후 document.activeElement 가
  // 타일 버튼이 아니게 되는 경우가 있어, activeElement 만으로는 값 반영이 누락된다(LOGISPRD-9511).
  const lastInteractionRef = useRef<'calendar' | 'input' | null>(null)

  const formattedValue = useMemo<DateRangeValue>(() => {
    if (!Array.isArray(value)) return [null, null]

    const [start, end] = value
    return [DatePickerUtils.getValidDate(start), DatePickerUtils.getValidDate(end)]
  }, [value])

  const showReset = useMemo(() => value?.some((v) => v), [value])
  const defaultOpts = useMemo(
    () => ({
      ...DatePickerUtils.getDatePickerDefaultOpts({ showTimeSelect, showAmPm, fixedCalendar }),
      rangeDivider: <div style={{ padding: '0 8px' }}>~</div>,
      calendarIcon: (
        <>
          {showReset && <InputResetButton onClick={() => onChange?.([null, null])} />}
          {DatePickerUtils.getDatePickerDefaultOpts({ showTimeSelect, showAmPm, fixedCalendar }).calendarIcon as React.ReactNode}
        </>
      ),
    }),
    [showTimeSelect, showAmPm, fixedCalendar, showReset],
  )

  const handleChange = useCallback(
    (v: DatePickerValue | DateRangeValue) => {
      if (!onChange) return
      if (!Array.isArray(v)) return

      const [start, end] = v
      if (!dayjs(start).isValid() || !dayjs(end).isValid()) return
      if (dayjs(end).isBefore(dayjs(start))) {
        return MToast.show('날짜 범위가 올바르지 않습니다.', { kind: 'error' })
      }

      // maxDateRange 초과 여부 검증
      if (maxDateRange && maxDateRange > 0) {
        const diffDays = dayjs(end).diff(dayjs(start), 'day')
        if (diffDays > maxDateRange) {
          return MToast.show(`최대 ${maxDateRange}일까지 선택 가능합니다.`, { kind: 'error' })
        }
      }

      setTemp(v)

      // 캘린더 타일 클릭으로 발생한 변경이면 즉시 폼에 반영한다.
      // (1) activeElement 가 타일 버튼인 경우 — 기존 동작(키보드 선택 포함). 모든 모드 공통.
      // (2) fixedCalendar(portal) 모드에서만, mousedown 으로 캡처한 출처가 'calendar' 인 경우 추가 반영.
      //     portal 시 타일 클릭 후 activeElement 가 타일이 아니게 되는 누락을 보강(LOGISPRD-9511).
      //     non-portal 화면(예: 정산 프로모션)은 이 경로를 타지 않아 기존 동작 100% 유지.
      const $active = document.activeElement as HTMLElement
      const isFromTileFocus = !!$active && $active.tagName === 'BUTTON' && $active.classList.contains(DATE_PICKER_TILE_CN)
      if (isFromTileFocus || (fixedCalendar && lastInteractionRef.current === 'calendar')) {
        onChange(v)
      }
    },
    [onChange, maxDateRange, fixedCalendar],
  )

  // mousedown 출처를 캡처한다. portal(fixedCalendar) 로 #root 에 렌더된 캘린더 타일의 이벤트도
  // React 합성 이벤트는 컴포넌트 트리를 따라 전파되므로 래퍼에서 캡처된다.
  const onPointerDownCapture = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const $target = e.target as HTMLElement | null
    lastInteractionRef.current = $target?.closest?.(`.${DATE_PICKER_TILE_CN}`) ? 'calendar' : 'input'
  }, [])

  const onCalendarOpen = useCallback(() => {
    if (!fixedCalendar) return
    DatePickerUtils.onFixPositionOfCal({ id, size, calCn, placement })
  }, [id, size, fixedCalendar, calCn, placement])

  const onClickInput = useCallback(() => {
    DatePickerUtils.onForceToOpen('range', calCn, onCalendarOpen)
    DatePickerUtils.onSelectAllOnDateInput()
  }, [calCn, onCalendarOpen])

  useEffect(
    function onInitTemp() {
      if (isEmpty(compact(temp)) && Array.isArray(formattedValue)) {
        setTemp(formattedValue)
      } else if (isEmpty(compact(formattedValue)) && !isEmpty(compact(temp))) {
        setTemp([null, null])
      }
    },
    [formattedValue],
  )

  useEffect(
    function onMount() {
      const onCloseCal = () => DatePickerUtils.onForceToClose('range', calCn)

      window.addEventListener('scroll', onCloseCal)
      return () => window.removeEventListener('scroll', onCloseCal)
    },
    [calCn],
  )

  const pickerStyle = useMemo(
    () => (height ? ({ '--datetime-picker-height': typeof height === 'number' ? `${height}px` : height } as React.CSSProperties) : undefined),
    [height],
  )

  return (
    <div style={pickerStyle} onMouseDownCapture={onPointerDownCapture}>
      <DateTimeRangePicker
        {...defaultOpts}
        id={id}
        value={formattedValue}
        className={cx(DATE_PICKER_CN, className, {
          [DATE_PICKER_SIZE_MD_CN]: size === 'md',
          [DATE_PICKER_SIZE_LG_CN]: size === 'lg',
        })}
        calendarClassName={cx(DATE_PICKER_CAL_CN, calCn, calendarClassName, { [DATE_PICKER_CAL_FIXED_CN]: fixedCalendar })}
        tileClassName={DATE_PICKER_TILE_CN}
        onChange={handleChange}
        onClick={onClickInput}
        onCalendarOpen={onCalendarOpen}
        onBlur={(e) => {
          const $target = e.target as HTMLInputElement
          if ($target && $target.tagName === 'INPUT' && onChange) onChange(temp)
        }}
        {...rest}
      />
    </div>
  )
}
